// IndexedDB Queue Manager for Offline Sync
import { openDB } from 'idb';

const DB_NAME = 'gym-tracker-sync';
const STORE_NAME = 'sync-queue';
const DB_VERSION = 1;

// Initialize IndexedDB
async function initDB() {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                store.createIndex('timestamp', 'timestamp');
                store.createIndex('userId', 'userId');
            }
        },
    });
}

// Generate unique ID
function generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Add operation to queue
export async function addToQueue(operation) {
    const db = await initDB();

    const queueItem = {
        id: generateId(),
        type: operation.type, // CREATE, UPDATE, DELETE
        collection: operation.collection,
        docId: operation.docId,
        data: operation.data,
        userId: operation.userId,
        timestamp: Date.now(),
        retries: 0,
        status: 'pending' // pending, syncing, error
    };

    await db.add(STORE_NAME, queueItem);


    return queueItem.id;
}

// Get all pending operations
export async function getQueue(userId = null) {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    let items = await store.getAll();

    // Filter by user if provided
    if (userId) {
        items = items.filter(item => item.userId === userId);
    }

    // Sort by timestamp
    items.sort((a, b) => a.timestamp - b.timestamp);

    return items;
}

// Get pending count
export async function getQueueCount(userId = null) {
    const items = await getQueue(userId);
    return items.filter(item => item.status === 'pending').length;
}

// Remove operation from queue
export async function removeFromQueue(id) {
    const db = await initDB();
    await db.delete(STORE_NAME, id);

}

// Update operation status
export async function updateQueueItem(id, updates) {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const item = await store.get(id);
    if (item) {
        Object.assign(item, updates);
        await store.put(item);
    }
}

// Clear entire queue (use with caution)
export async function clearQueue(userId = null) {
    const db = await initDB();

    if (userId) {
        // Clear only user's items
        const items = await getQueue(userId);
        const tx = db.transaction(STORE_NAME, 'readwrite');
        for (const item of items) {
            await tx.store.delete(item.id);
        }
    } else {
        // Clear all
        await db.clear(STORE_NAME);
    }
}

// Remove all queue items for a specific document (used when document is deleted)
export async function removeQueueItemsByDocId(docId, collection = null) {
    const db = await initDB();
    const items = await getQueue();

    const tx = db.transaction(STORE_NAME, 'readwrite');
    for (const item of items) {
        if (item.docId === docId && (!collection || item.collection === collection)) {
            await tx.store.delete(item.id);
        }
    }
}

// Get failed operations (for retry)
export async function getFailedOperations(userId = null) {
    const items = await getQueue(userId);
    return items.filter(item => item.status === 'error' && item.retries < 3);
}

export default {
    addToQueue,
    getQueue,
    getQueueCount,
    removeFromQueue,
    updateQueueItem,
    clearQueue,
    removeQueueItemsByDocId,
    getFailedOperations
};
